import { ChangeDetectorRef, Component, Host, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[app-menuitem]',
    template: `
		<ng-container>
            <div *ngIf="root && item.visible !== false" class="layout-menuitem-root-text">{{item.label}}</div>
			<a *ngIf="(!item.routerLink || item.items) && item.visible !== false" [attr.href]="item.url" (click)="itemClick($event)"
			   [ngClass]="item.class" [attr.target]="item.target" tabindex="0" pRipple>
				<i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
				<span class="layout-menuitem-text">{{item.label}}</span>
				<i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
			</a>
			<a *ngIf="(item.routerLink && !item.items) && item.visible !== false" (click)="itemClick($event)" [ngClass]="item.class"
         [routerLink]="item.routerLink" routerLinkActive="active-route"
         [routerLinkActiveOptions]="
              (item.routerLink[0] === '/')
              ? { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }
              : { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }
          "
          [class.active-route]="forceActiveRouteClass"
          [fragment]="item.fragment" [queryParamsHandling]="item.queryParamsHandling" [preserveFragment]="item.preserveFragment"
          [skipLocationChange]="item.skipLocationChange" [replaceUrl]="item.replaceUrl" [state]="item.state" [queryParams]="item.queryParams"
          [attr.target]="item.target" tabindex="0" pRipple>
        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{item.label}}</span>
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
      </a>

			<ul *ngIf="item.items && item.visible !== false" [@children]="submenuAnimation">
				<ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
					<li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child.badgeClass"></li>
				</ng-template>
			</ul>
		</ng-container>
    `,
    animations: [
        trigger('children', [
            state('collapsed', style({
                height: '0'
            })),
            state('expanded', style({
                height: '*'
            })),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class AppMenuitemComponent implements OnInit, OnDestroy {

    @Input() item: any;

    @Input() index!: number;

    @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

    @Input() parentKey!: string;

    active = false;
    // Ensures the anchor receives the visual active class when matched via extra paths
    forceActiveRouteClass = false;

    menuSourceSubscription: Subscription;

    menuResetSubscription: Subscription;

    key: string = "";

    constructor(public layoutService: LayoutService, private cd: ChangeDetectorRef, public router: Router, private menuService: MenuService) {
        this.menuSourceSubscription = this.menuService.menuSource$.subscribe(value => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active = (value.key === this.key || value.key.startsWith(this.key + '-')) ? true : false;
                }
                else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active = false;
                    }
                }
            });
        });

        this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                // Recompute for both leaf and parent items on every navigation
                this.updateActiveStateFromRoute();
            });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

        // Always compute initial active state
        this.updateActiveStateFromRoute();
    }

    updateActiveStateFromRoute() {
        const currentUrl = this.router.url;

        let isActive = false;

        // Leaf item (has routerLink and no children)
        if (this.item.routerLink && !this.item.items) {
            const link = this.item.routerLink[0];
            isActive = this.router.isActive(link, {
                paths: link === '/' ? 'exact' : 'subset',
                queryParams: 'ignored',
                matrixParams: 'ignored',
                fragment: 'ignored'
            });

            // Hard fallback for assignment list/detail relationship
            if (!isActive && link === '/batch-management/assignment-list' && (currentUrl.startsWith('/batch-management/assignment/') || currentUrl === '/batch-management/assignment')) {
                isActive = true;
            }

            // Additionally, support extra activation paths via item.activeMatchPaths
            if (!isActive && Array.isArray(this.item.activeMatchPaths) && this.item.activeMatchPaths.length > 0) {
                for (const p of this.item.activeMatchPaths) {
                    if (typeof p === 'string' && p.length > 0) {
                        if (currentUrl.includes(p) || this.router.isActive(p, { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' })) {
                            isActive = true;
                            this.forceActiveRouteClass = true;
                            break;
                        }
                    }
                }
            }

            this.forceActiveRouteClass = this.forceActiveRouteClass || isActive;
        }

        // Parent item (has children). Keep expanded if any child is active
        if (this.item.items && Array.isArray(this.item.items)) {
            for (const child of this.item.items) {
                const childLink = child?.routerLink?.[0];
                const childMatchesLink = childLink ? (currentUrl.startsWith(childLink) || this.router.isActive(childLink, { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' })) : false;
                const childMatchesExtra = Array.isArray(child?.activeMatchPaths) && child.activeMatchPaths.some((p: string) => typeof p === 'string' && p.length > 0 && currentUrl.includes(p));
                if (childMatchesLink || childMatchesExtra) {
                    this.active = true;
                    this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
                    break;
                }
            }
        }

        // No-op here: this hard fallback is handled earlier for leaf items where 'link' is defined

        // Additionally, support extra activation paths via item.activeMatchPaths at the component level (if needed)
        if (!isActive && Array.isArray(this.item.activeMatchPaths) && this.item.activeMatchPaths.length > 0) {
            for (const p of this.item.activeMatchPaths) {
                if (typeof p === 'string' && p.length > 0) {
                    const match = this.router.isActive(p, {
                        paths: 'subset',
                        queryParams: 'ignored',
                        matrixParams: 'ignored',
                        fragment: 'ignored'
                    }) || currentUrl.includes(p);
                    if (match) {
                        isActive = true;
                        this.forceActiveRouteClass = true;
                        break;
                    }
                }
            }
        }

        if (!isActive) {
            this.forceActiveRouteClass = false;
            this.active = false;
        }
        else {
            // Mark this item active as well so the li gets active-menuitem class
            this.active = true;
        }

        if (isActive) {
            this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;
        }

        this.menuService.onMenuStateChange({ key: this.key });
    }

    get submenuAnimation() {
        return this.root ? 'expanded' : (this.active ? 'expanded' : 'collapsed');
    }

    @HostBinding('class.active-menuitem')
    get activeClass() {
        return this.active && !this.root;
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }
}
